/*
 * Shop the Look Drawer
 * Uses the native dialog element for modal focus handling and Escape support.
 * Drawer product images hydrate only after their associated look is opened.
 */
(() => {
  'use strict';

  if (window.ShopTheLookDrawer) {
    window.ShopTheLookDrawer.initializeAll();
    return;
  }

  const rootSelector = '[data-shop-the-look-drawer]';
  const instanceKey = '__shopTheLookDrawerInstance';

  function hydratePanel(panel) {
    if (!panel || panel.dataset.shopTheLookHydrated === 'true') return;

    panel.querySelectorAll('[data-shop-the-look-product-image]').forEach((image) => {
      if (!image.dataset.src) return;

      image.src = image.dataset.src;
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.sizes) image.sizes = image.dataset.sizes;

      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-sizes');
    });

    panel.dataset.shopTheLookHydrated = 'true';
  }

  function createController(root) {
    const dialog = root.querySelector('[data-shop-the-look-dialog]');
    const triggers = Array.from(root.querySelectorAll('[data-shop-the-look-drawer-trigger]'));
    const panels = Array.from(root.querySelectorAll('[data-shop-the-look-product-panel]'));
    const closeButton = dialog && dialog.querySelector('[data-shop-the-look-drawer-close]');
    const drawerBody = dialog && dialog.querySelector('.shop-the-look-drawer__drawer-body');

    if (!dialog || !triggers.length || !panels.length) return null;

    let lastTrigger = null;

    function setActivePanel(panelId) {
      const activePanel = panels.find((panel) => panel.id === panelId);
      if (!activePanel) return false;

      panels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });

      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', trigger.dataset.shopTheLookPanelId === panelId ? 'true' : 'false');
      });

      hydratePanel(activePanel);
      return true;
    }

    function open(trigger) {
      const panelId = trigger && trigger.dataset.shopTheLookPanelId;
      if (!panelId || !setActivePanel(panelId)) return;

      lastTrigger = trigger;

      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      }

      document.documentElement.classList.add('shop-the-look-drawer-is-open');

      window.requestAnimationFrame(() => {
        if (closeButton) closeButton.focus({ preventScroll: true });
      });
    }

    function close({ restoreFocus = true } = {}) {
      if (!dialog.open) return;

      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        finishClose(restoreFocus);
      }
    }

    function finishClose(restoreFocus = true) {
      triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

      if (!document.querySelector('dialog[open]')) {
        document.documentElement.classList.remove('shop-the-look-drawer-is-open');
      }

      if (restoreFocus && lastTrigger && document.contains(lastTrigger)) {
        lastTrigger.focus({ preventScroll: true });
      }
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => open(trigger));
    });

    if (closeButton) {
      closeButton.addEventListener('click', () => close());
    }

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    dialog.addEventListener('close', () => finishClose());

    dialog.addEventListener('cancel', () => {
      // Native dialog closes on Escape. The close handler restores focus and state.
    });

    function preventBackgroundScroll(event) {
      if (!dialog.open) return;

      const isDrawerBodyTarget = event.target instanceof Node
        && drawerBody
        && drawerBody.contains(event.target);

      if (!isDrawerBodyTarget) event.preventDefault();
    }

    // Do not set overflow: hidden on <body>: TYR's header uses sticky positioning
    // within the page scroll container, which that rule disrupts. The modal's
    // backdrop already receives pointer input; these listeners also prevent wheel
    // and touch scrolling from reaching the dimmed page behind it.
    document.addEventListener('wheel', preventBackgroundScroll, { capture: true, passive: false });
    document.addEventListener('touchmove', preventBackgroundScroll, { capture: true, passive: false });

    const controller = { open, close, root };

    root.addEventListener('shopify:block:select', (event) => {
      const selectedLook = event.target.closest('.shop-the-look-drawer__look');
      const trigger = selectedLook && selectedLook.querySelector('[data-shop-the-look-drawer-trigger]');
      if (trigger) open(trigger);
    });

    return controller;
  }

  function initialize(root) {
    if (!root || root[instanceKey]) return root && root[instanceKey];

    const controller = createController(root);
    if (controller) root[instanceKey] = controller;

    return controller;
  }

  function initializeAll(container = document) {
    if (container.matches && container.matches(rootSelector)) initialize(container);
    container.querySelectorAll(rootSelector).forEach(initialize);
  }

  window.ShopTheLookDrawer = { initializeAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches(rootSelector)
      ? event.target
      : event.target.querySelector && event.target.querySelector(rootSelector);

    if (root && root[instanceKey]) root[instanceKey].close({ restoreFocus: false });
  });
})();
